import React from 'react';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames';

import {
  SCROLL_LOCK_IGNORE_CLASS, disableBodyScroll, enableBodyScroll
} from 'helpers/ScrollHelpers';
import { useMediaQuery } from 'hooks/useMediaQuery';

import ModalBody from '../Modal/ModalBody';

import type { DrawerPosition } from './Drawer';
import type { Props as ModalHeaderProps } from '../Modal/ModalHeader';

import type { Breakpoint, IModalProps } from 'types';

import baseStyles from './Drawer.module.scss';
import styles from './PersistentDrawer.module.scss';

interface BreakpointSpecificProps {
  hidden?: boolean
}

export interface Props extends IModalProps{
  className?: string,
  contentClassName?: string,
  innerClassName?: string,
  bodyClassName?: string,
  //
  position: DrawerPosition,
  title: string | React.ReactElement,
  breakpoint?: Breakpoint,
  desktopProps?: BreakpointSpecificProps,
  responsiveProps?: BreakpointSpecificProps,
  headerProps?: ModalHeaderProps,
  //
  children: React.ReactElement | React.ReactElement[]
}

const PersistentDrawer = (props: Props) => {
  const {
    isOpen,
    //
    className,
    contentClassName,
    innerClassName,
    bodyClassName,
    //
    breakpoint = 'lg',
    desktopProps,
    responsiveProps,
    headerProps,
    //
    position,
    title,
    //
    children,
    //
    onClose = () => {},
  } = props;

  // Refs

  const bodyRef = React.useRef(null);

  // Hooks

  const isBreakpoint = useMediaQuery(`(max-width: ${styles[`breakpoint_${breakpoint}`]})`);

  // Handlers

  const onEntering = () => {
    disableBodyScroll(
      bodyRef.current,
      { reserveScrollBarGap: !isBreakpoint }
    );
  };

  const onExited = () => {
    enableBodyScroll(
      bodyRef.current
    );
  };

  // Effects

  React.useEffect(() => {
    return () => {
      if (isOpen) {
        enableBodyScroll(
          bodyRef.current
        );
      }
    };
  }, [isOpen]);

  // Props

  const {
    hidden
  } = (isBreakpoint ? responsiveProps : desktopProps) || {};

  // Hidden

  if (hidden) return null;

  // Render

  return (
    <CSSTransition
      in={isOpen}
      timeout={{
        enter: 400,
        exit: 200,
      }}
      classNames={{
        enter: styles.enter,
        enterActive: styles.enterActive,
        enterDone: styles.enterDone,
        //
        exit: styles.exit,
        exitActive: styles.exitActive,
        exitDone: styles.exitDone,
      }}
      onEntering={onEntering}
      onExited={onExited}
    >
      <div
        className={classnames(
          baseStyles.root,
          baseStyles.drawer,
          styles.root,
          styles.drawer,
          styles.persistent,
          { [styles[position]]: position },
          className,
        )}
      >

        {/* Background */}
        <div
          className={classnames(
            styles.background,
          )}
          onClick={() => {
            if (isOpen) {
              onClose();
            }
          }}
        />

        {/* Content */}
        <div
          className={classnames(
            baseStyles.content,
            styles.content,
            styles.persistent,
            'on-click-outside-ignore',
            contentClassName,
          )}
        >

          {/* Inner */}
          <div
            className={classnames(
              baseStyles.inner,
              styles.inner,
              innerClassName,
            )}
          >

            {/* Body */}
            <ModalBody
              ref={bodyRef}
              className={classnames(
                styles.body,
                SCROLL_LOCK_IGNORE_CLASS,
                bodyClassName,
              )}
              headerProps={{
                title,
                withCloseButton: true,
                breakpoint,
                size: 'medium',
                ...headerProps,
                desktopProps: {
                  size: 'medium',
                  closeIconProps: {
                    size: 20,
                    strokeWidth: 2.5,
                  },
                  ...headerProps?.desktopProps,
                },
                responsiveProps: {
                  size: 'medium',
                  closeIconProps: {
                    size: 20,
                    strokeWidth: 2.5,
                  },
                  ...headerProps?.responsiveProps
                },
                onClose
              }}
            >
              {children}
            </ModalBody>
          </div>
        </div>
      </div>
    </CSSTransition>
  );
};

export default PersistentDrawer;
